import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  createCheckoutSession,
  getDealById,
} from "../../services/api";
import { DealResponseDto } from "../../types/types";
import DealViewContent from "./dealViewContent";
import { useDealViewRenderers } from "./useDealViewRenderers";

const downloadBlob = (blob: Blob, fileName: string) => {
  const downloadUrl = window.URL.createObjectURL(blob);
  try {
    const anchorElement = document.createElement("a");
    anchorElement.href = downloadUrl;
    anchorElement.download = fileName;
    anchorElement.click();
  } finally {
    window.URL.revokeObjectURL(downloadUrl);
  }
};

const cleanDealId = (value?: string): string | null => {
  const text = String(value || "").trim();
  const smallText = text.toLowerCase();

  if (!text) return null;

  if (smallText === "nan" || smallText === "undefined" || smallText === "null") {
    return null;
  }

  return text;
};

const ViewDealPage: React.FC = () => {
  const { t, i18n } = useTranslation("viewDeal");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [deal, setDeal] = useState<DealResponseDto | null>(null);
  const [loading, setLoading] = useState(true); 
  const [activeTab, setActiveTab] = useState("general");
  const [downloadingAgreement, setDownloadingAgreement] = useState(false);

  const paymentPollRef = useRef<number | null>(null);
  const paymentRefreshGuardRef = useRef(false);
  const paymentLocksRef = useRef<Set<number>>(new Set());

  const { currentUserId, loggedInUserName, userProfileType } = useMemo(() => {
    const rawLoginResponse = localStorage.getItem("loginResponse") || "{}";
    const loginResponse = JSON.parse(rawLoginResponse);
    return {
      currentUserId: loginResponse.id?.toString(),
      loggedInUserName: loginResponse.username || "",
      userProfileType: loginResponse.profileType || "",
    };
  }, []);

  const isFighter = useMemo(
    () => userProfileType === "FIGHTER",
    [userProfileType],
  );

  const isManager = useMemo(() => {
    if (!deal || !currentUserId) return false;
    return (deal.acceptedBy?.id?.toString() || "") !== currentUserId;
  }, [deal, currentUserId]);

  const hasUserRated = useMemo(() => {
    if (!deal?.ratingResponse || !currentUserId) return false;
    return deal.ratingResponse.some(
      (rating) => rating.ratedBy?.id?.toString() === currentUserId,
    );
  }, [deal?.ratingResponse, currentUserId]);

  const canPayComponents = useMemo(() => {
    return [
      "ACCEPTED",
      "CONFIRMED",
      "WAITING_FOR_RESULT",
      "RESULT_UPDATED",
      "RESULT_CONFIRMED",
      "FIGHTER_RATED",
      "MANAGER_RATED",
    ].includes(deal?.status || "");
  }, [deal]);

  const refreshDeal = useCallback(async () => {
    const dealId = cleanDealId(id);
    if (!dealId) return;

    try {
      const updatedDeal = await getDealById(dealId);
      setDeal(updatedDeal);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t("failedToRefreshDeal"));
    }
  }, [id, t]);

  const formData: any = deal?.formData ?? {};
  const { renderDynamicObject, renderSummary, tr  } = useDealViewRenderers({
    deal,
    formData,
    t,
    refreshDeal,
  });

  const refreshDealAfterPayment = useCallback(async () => {
    if (paymentRefreshGuardRef.current) return;
    paymentRefreshGuardRef.current = true;
    try {
      await refreshDeal();
    } finally {
      window.setTimeout(() => {
        paymentRefreshGuardRef.current = false;
      }, 1200);
    }
  }, [refreshDeal]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasPaidQueryParam = !!urlParams.get("paid");

    if (hasPaidQueryParam) {
      void refreshDealAfterPayment();
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
    }
  }, [refreshDealAfterPayment]);

  useEffect(() => {
    const handlePostMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      const messageType = event.data?.type ?? event.data?.status;
      if (messageType === "PAYMENT_SUCCESS") {
        void refreshDealAfterPayment();
        toast.success(t("paymentSuccessful"));
      }
    };

    window.addEventListener("message", handlePostMessage);
    return () => window.removeEventListener("message", handlePostMessage);
  }, [refreshDealAfterPayment, t]);

  useEffect(() => {
    return () => {
      if (paymentPollRef.current) {
        window.clearInterval(paymentPollRef.current);
        paymentPollRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const dealId = cleanDealId(id);
    if (!dealId) {
      setLoading(false);
      setDeal(null);
      return;
    }

    const fetchDeal = async () => {
      try {
        setLoading(true);
        const dealResponse = await getDealById(dealId);
        setDeal(dealResponse);
      } catch (error: any) {
        toast.error(error?.response?.data?.message || t("failedToLoadDeal"));
        navigate("/deals");
      } finally {
        setLoading(false);
      }
    };

    fetchDeal();
  }, [id, navigate, t]);

  useEffect(() => {
    const storedLanguage = localStorage.getItem("i18nextLng") || "en";
    i18n.changeLanguage(storedLanguage);
  }, [i18n]);

  useEffect(() => {
    if (loading || !deal) return;

    const urlParams = new URLSearchParams(window.location.search);
    const highlightSection = urlParams.get("highlight");
    if (!highlightSection) return;

    setActiveTab("general");

    const sectionIdMap: Record<string, string> = {
      documentRequirement: "document-requirement-section",
    };
    const targetId = sectionIdMap[highlightSection] || highlightSection;

    window.setTimeout(() => {
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 300);

    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, "", cleanUrl);
  }, [loading, deal]);

  const handleDownloadAgreement = async () => {
    if (!deal?.agreementFile?.fileUrl) return;
    setDownloadingAgreement(true);
    try {
      const agreementResponse = await fetch(deal.agreementFile.fileUrl);
      if (!agreementResponse.ok) throw new Error(t("failedToDownloadAgreement"));
      const agreementBlob = await agreementResponse.blob();
      downloadBlob(
        agreementBlob,
        deal.agreementFile.fileName || "agreement.pdf",
      );
    } catch (error: any) {
      toast.error(error?.message || t("failedToDownloadAgreement"));
    } finally {
      setDownloadingAgreement(false);
    }
  };

  const handlePayComponent = async (componentPaymentId: number) => {
    if (!deal?.id || !loggedInUserName) return;
    if (paymentLocksRef.current.has(componentPaymentId)) return;

    const componentPayment = deal.componentPayments?.find(
      (payment: any) => Number(payment.id) === Number(componentPaymentId),
    );

    if (!componentPayment || componentPayment.status !== "PENDING") {
      toast.error(t("paymentNotAvailableForCheckout"));
      return;
    }

    paymentLocksRef.current.add(componentPaymentId);

    try {
      const checkoutResponse = await createCheckoutSession(
        loggedInUserName,
        deal.id,
        componentPaymentId,
      );

      const checkoutUrl =
        (checkoutResponse as any)?.checkoutUrl ||
        (checkoutResponse as any)?.url ||
        "";

      if (!checkoutUrl) {
        throw new Error(t("checkoutUrlNotAvailable"));
      }

      if (paymentPollRef.current) {
        window.clearInterval(paymentPollRef.current);
        paymentPollRef.current = null;
      }

      const paymentWindow = window.open(checkoutUrl, "_blank");

      if (!paymentWindow) {
        window.location.assign(checkoutUrl);
        return;
      }

      paymentPollRef.current = window.setInterval(() => {
        if (paymentWindow.closed) {
          if (paymentPollRef.current) {
            window.clearInterval(paymentPollRef.current);
            paymentPollRef.current = null;
          }
          void refreshDealAfterPayment();
        }
      }, 1000);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          t("componentPaymentFailed"),
      );
    } finally {
      paymentLocksRef.current.delete(componentPaymentId);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );

  if (!deal)
    return (
      <div className="text-center p-10">
        <p>{t("dealNotFound")}</p>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 rounded"
        >
          {t("back")}
        </button>
      </div>
    );

return (
    <DealViewContent
      deal={deal}
      formData={formData}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      t={t}
      isFighter={isFighter}
      canPayComponents={canPayComponents}
      handlePayComponent={handlePayComponent}
      currentUserId={currentUserId}
      isManager={isManager}
      hasUserRated={hasUserRated}
      refreshDeal={refreshDeal}
      downloadingAgreement={downloadingAgreement}
      handleDownloadAgreement={handleDownloadAgreement}
      renderDynamicObject={renderDynamicObject}
      renderSummary={renderSummary}
      tr={tr}   
    />
  );
};

export default ViewDealPage;
